/**
 * Desktop (Tauri) platform adapter.
 *
 * Wraps Tauri's invoke/listen APIs and provides the full desktop feature set.
 * This is the only adapter that supports AmiBroker and custom window chrome.
 */
import { PlatformAdapter } from "./platform.interface";
import { createPortableUuid } from "./uuid";

export class DesktopPlatform implements PlatformAdapter {
  readonly type = "desktop" as const;
  readonly supportsCustomTitleBar = true;
  readonly supportsAmiBroker = true;
  readonly maxChartPanels = 6;
  readonly fullDrawingTools = true;
  readonly supportsLocalBacktest = true;
  readonly tickBatchIntervalMs = 100;

  // Lazy-loaded Tauri APIs
  private tauriCore: typeof import("@tauri-apps/api/core") | null = null;
  private tauriEvent: typeof import("@tauri-apps/api/event") | null = null;

  private async loadTauriCore() {
    if (!this.tauriCore) {
      this.tauriCore = await import("@tauri-apps/api/core");
    }
    return this.tauriCore;
  }

  private async loadTauriEvent() {
    if (!this.tauriEvent) {
      this.tauriEvent = await import("@tauri-apps/api/event");
    }
    return this.tauriEvent;
  }

  // ─── Device Identity ─────────────────────────────────

  async getDeviceId(): Promise<string> {
    try {
      const { invoke } = await this.loadTauriCore();
      return await invoke<string>("get_machine_id");
    } catch {
      // Fallback: generate and persist a UUID
      let id = localStorage.getItem("_desktop_device_id");
      if (!id) {
        id = createPortableUuid();
        localStorage.setItem("_desktop_device_id", id);
      }
      return id;
    }
  }

  getDeviceName(): string {
    return `Desktop (${navigator.platform || "Windows"})`;
  }

  // ─── Window Management ───────────────────────────────

  async minimizeWindow(): Promise<void> {
    try {
      const { getCurrentWindow } = await import("@tauri-apps/api/window");
      await getCurrentWindow().minimize();
      return;
    } catch {
      const { invoke } = await this.loadTauriCore();
      await invoke("minimize_window");
    }
  }

  async maximizeWindow(): Promise<void> {
    try {
      const { getCurrentWindow } = await import("@tauri-apps/api/window");
      const win = getCurrentWindow();
      const maximized = await win.isMaximized();
      if (maximized) {
        await win.unmaximize();
      } else {
        await win.maximize();
      }
      return;
    } catch {
      const { invoke } = await this.loadTauriCore();
      await invoke("maximize_window");
    }
  }

  async closeWindow(): Promise<void> {
    try {
      const { invoke } = await this.loadTauriCore();
      await invoke("close_window");
      return;
    } catch {
      const { invoke } = await this.loadTauriCore();
      await invoke("close_window");
    }
  }

  // ─── Storage ─────────────────────────────────────────

  async getStorageItem(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  }

  async setStorageItem(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  }

  async removeStorageItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  }

  // ─── Notifications ───────────────────────────────────

  async showNotification(title: string, body: string): Promise<void> {
    try {
      // Use Tauri notification plugin
      const notif = await import("@tauri-apps/plugin-notification");
      await notif.sendNotification({ title, body });
    } catch {
      // Fallback to Web Notification API
      if (Notification.permission === "granted") {
        new Notification(title, { body });
      }
    }
  }

  async requestNotificationPermission(): Promise<boolean> {
    try {
      const notif = await import("@tauri-apps/plugin-notification");
      const perm = await notif.requestPermission();
      return perm === "granted";
    } catch {
      const perm = await Notification.requestPermission();
      return perm === "granted";
    }
  }

  async getPushToken(): Promise<string | null> {
    // Desktop doesn't use FCM push tokens
    return null;
  }

  // ─── Lifecycle Events ────────────────────────────────

  onAppClose(callback: () => void): () => void {
    let unlisten: (() => void) | null = null;

    this.loadTauriEvent().then((events) => {
      events.listen("exit-app", () => callback()).then((fn) => {
        unlisten = fn;
      });
    });

    return () => {
      if (unlisten) unlisten();
    };
  }

  onAppResume(callback: () => void): () => void {
    // Desktop doesn't hibernate — use visibility change as proxy
    const handler = () => {
      if (document.visibilityState === "visible") callback();
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }

  // ─── OAuth / Auth ─────────────────────────────────────────────────────────

  getOAuthClientId(): string {
    // Desktop-specific client ID injected via the Vite HTML shim (REACT_APP_OAUTH_CLIENT_ID_DESKTOP).
    // Set in .env (dev) or .env.production (prod). Fallback matches application[-prod].properties.
    return (
      process.env.REACT_APP_OAUTH_CLIENT_ID_DESKTOP ||
      process.env.REACT_APP_OAUTH_CLIENT_ID ||
      process.env.REACT_APP_CLIENTID ||
      "marketview-desktop-client"
    );
  }

  getOAuthRedirectUri(): string {
    // Authorization is API-only, but OAuth still requires a stable registered
    // redirect identifier for code binding and token exchange.
    return (
      process.env.REACT_APP_OAUTH_REDIRECT_URI_DESKTOP ||
      process.env.REACT_APP_OAUTH_REDIRECT_URI ||
      "http://tauri.localhost/"
    );
  }

}
